import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertTitle, AlertView, Poppins } from 'src/common';
import _ from 'lodash';
import CurrencyInput from 'src/components/form/CurrencyInput';
import Input from 'src/components/form/Input';
import { CsvVariable } from 'src/components/VariableCSVReader/types';

const getValueError = (value: number | null): boolean => {
  return value === null || value < 0;
};

const snakeToDisplayName = (name: string): string => {
  const displayName = name.replace(/^#/, '').replace(/_/g, ' ');
  return displayName.charAt(0).toUpperCase() + displayName.slice(1);
};

const VariableRow = styled.div`
  display: grid;
  grid-template-columns: 16px 0.7fr 0.5fr auto 142px 1fr;
  gap: 12px;
  align-items: center;
  /* border: 1px solid red; */
  max-width: 800px;

  .grid-item {
    /* outline: 1px solid blue; */

    &__divider {
      background-color: ${({ theme }) => theme.colors.white};
      height: 1px;
      z-index: 1;
      position: relative;
    }

    &__currency-input {
      display: flex;
      grid-template-columns: repeat(auto-fit, minmax(10px, 1fr));
      grid-gap: 10px;
      align-items: center;
      justify-content: flex-end;
    }
  }

  .var-chip {
    padding: 0 12px;
    min-height: 32px;
    display: flex;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.aliceBlue};
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    border-radius: 16px;
    position: relative;
    z-index: 1;
  }
`;

export interface ManualVariable {
  name: string;
  snakeName: string;
  value: number | null;
  currency: boolean;
}

interface MissingVariablesFormProps {
  missingVariableNames: string[];
  csvVariables: CsvVariable[];
  manualVariables: ManualVariable[];
  onManualVariablesChange: (variables: ManualVariable[]) => void;
  onValidityChange?: (hasErrors: boolean) => void;
}

export const MissingVariablesForm: React.FC<MissingVariablesFormProps> = ({
  missingVariableNames,
  csvVariables,
  manualVariables,
  onManualVariablesChange,
  onValidityChange,
}) => {
  const [touchedValues, setTouchedValues] = useState<Set<string>>(new Set());

  const stillMissingNames = missingVariableNames.filter((name) => {
    const snakeName = _.snakeCase(name);
    return !csvVariables.some((csv) => _.snakeCase(csv.name) === snakeName);
  });

  const displayVariables = manualVariables.filter((v) =>
    stillMissingNames.some((name) => _.snakeCase(name) === v.snakeName),
  );

  useEffect(() => {
    const existingNames = new Set(manualVariables.map((v) => v.snakeName));
    const newVariables: ManualVariable[] = [];

    stillMissingNames.forEach((originalName) => {
      const snakeName = _.snakeCase(originalName);
      if (!existingNames.has(snakeName)) {
        newVariables.push({
          name: snakeToDisplayName(originalName),
          snakeName,
          value: null,
          currency: false,
        });
      }
    });

    if (newVariables.length > 0) {
      onManualVariablesChange([...manualVariables, ...newVariables]);
    }
  }, [stillMissingNames.join(',')]);

  useEffect(() => {
    if (!onValidityChange) return;
    const hasErrors = displayVariables.some((v) => getValueError(v.value));
    onValidityChange(hasErrors);
  }, [displayVariables, onValidityChange]);

  if (stillMissingNames.length === 0) return null;

  const handleValueChange = (snakeName: string, value: string) => {
    const numValue = value === '' ? null : Number(value);
    onManualVariablesChange(
      manualVariables.map((v) =>
        v.snakeName === snakeName ? { ...v, value: isNaN(numValue as number) ? null : numValue } : v,
      ),
    );
  };

  const handleCurrencyChange = (snakeName: string, currency: boolean) => {
    onManualVariablesChange(manualVariables.map((v) => (v.snakeName === snakeName ? { ...v, currency } : v)));
  };

  const handleValueBlur = (snakeName: string) => {
    setTouchedValues((prev) => new Set(prev).add(snakeName));
    const variable = manualVariables.find((v) => v.snakeName === snakeName);
    if (variable) {
      handleValueChange(snakeName, variable.value?.toString() ?? '');
    }
  };

  if (displayVariables.length === 0) return null;

  return (
    <AlertView>
      <AlertTitle>
        <Poppins px={14} color="statusRed" weight={600} style={{ fontStyle: 'italic' }}>
          The following variables are unrecognised. Please add names and values to these variables.
        </Poppins>
      </AlertTitle>

      {displayVariables.map((el) => {
        const valueError = getValueError(el.value);
        const isValueTouched = touchedValues.has(el.snakeName);
        return (
          <VariableRow key={el.snakeName}>
            <div className="grid-item"></div>
            <div className="grid-item">
              <Poppins px={14} weight={600} color="statusRed" style={{ fontStyle: 'italic' }}>
                #{el.snakeName}
              </Poppins>
            </div>
            <div className="grid-item">
              <div className="grid-item__divider" />
            </div>

            <div className="grid-item">
              <div className="var-chip">
                <Poppins px={14} weight={500} nowrap>
                  {el.name}
                </Poppins>
              </div>
            </div>
            <div className="grid-item">
              <div className="grid-item__currency-input">
                <Poppins px={14} weight={500} color="cflowerBlue" nowrap className="currency-label">
                  CURRENCY
                </Poppins>
                <Input
                  checked={el.currency}
                  onChange={(e) => {
                    handleCurrencyChange(el.snakeName, e.target.checked);
                  }}
                  className="switch"
                  type="checkbox"
                />
              </div>
            </div>

            <div className="grid-item">
              <CurrencyInput
                placeholder={`Enter value *`}
                value={el.value}
                currency={el.currency}
                onValueChange={(e) => handleValueChange(el.snakeName, e.floatValue.toString())}
                onBlur={() => handleValueBlur(el.snakeName)}
                fullWidth={true}
                error={isValueTouched && valueError}
              />
            </div>
          </VariableRow>
        );
      })}
    </AlertView>
  );
};
